import { useState } from "react";
import Slider from "../../UIComponent/rangeSlider";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from "chart.js";
import convertNumberToWords from "../UtilFunctions/convertToWords";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function EmiCalculator() {
  const [amount, setAmount] = useState(1550000);
  const [interest, setInterest] = useState(12.75);
  const [tenure, setTenure] = useState(36); // months

  // EMI Calculation
  const monthlyInterest = interest / (12 * 100);
  const emi =
    (amount * monthlyInterest * Math.pow(1 + monthlyInterest, tenure)) /
    (Math.pow(1 + monthlyInterest, tenure) - 1);

  const totalPayable = emi * tenure;
  const totalInterest = totalPayable - amount;

  // Pie chart config
  const chartData = {
    labels: ["Principal Amount", "Interest Payable"],
    datasets: [
      {
        data: [amount, totalInterest],
        backgroundColor: ["#ccfce2", "#00ff95"],
        borderWidth: 0
      }
    ]
  };

  const chartOptions = {
    cutout: "70%",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ₹${ctx.raw.toLocaleString()}`
        }
      }
    }
  };

  function capitalizeFirst(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function capitalizeAllWords(str) {
    return str
      .split(" ")
      .map((word) => capitalizeFirst(word))
      .join(" ");
  }

  return (

    <section
      id="emiCalculator"
      style={{ fontFamily: "Atkinson-regular" }}
      className="min-h-screen bg-[#0d0d0d] text-white 
              px-[25px]
              md:px-[100px]  lg:px-[130px]  
             
             py-10 sm:py-[60px] md:py-[80px] 
             flex flex-col md:flex-row items-center justify-center gap-[50px]"
    >


      {/* LEFT */}
      <div className="w-full md:w-[60%]">
        <h1
          style={{ fontFamily: "myFirstFont7" }}
          className="text-[36px] sm:text-[48px] md:text-[60px] font-bold mb-1"
        >
          EMI Calculator
        </h1>
        <p
          style={{ fontFamily: "Atkinson-regular" }}
          className="text-[#12F280] text-[16px] sm:text-[18px]"
        >
          Calculate EMI for your Personal Loan
        </p>

        {/* Loan Amount Input */}
        <div className="mt-[30px]">
          <label className="block mb-2">Loan Amount</label>
          <input
            type="text"
            value={amount.toLocaleString("en-IN", {
              maximumFractionDigits: 0
            })}
            onChange={(e) => {
              const val = parseInt(e.target.value.replace(/[^0-9]/g, ""));
              if (!isNaN(val)) setAmount(val);
            }}
            className="w-full bg-[#FFFFFF] p-3 text-black rounded-lg text-xl"
          />

          <p className="mt-2 text-[13px] text-[#717171]">
            {capitalizeAllWords(convertNumberToWords(amount, true))}
          </p>

          <div className="flex gap-2 mt-2 flex-wrap">
            {[100000, 500000, 1000000, 1500000, 2000000].map((amt) => (
              <button
                key={amt}
                onClick={() => setAmount(amt)}
                className={`px-4 py-1 rounded-full text-[12px] ${amount === amt
                  ? "bg-[#12F280] text-black"
                  : "bg-[#363636] text-[#818181]"
                  }`}
              >
                {amt / 100000}L
              </button>
            ))}
          </div>
        </div>

        <div className="my-[30px] border-t border-[#3B3B3B]" />

        {/* Sliders */}
        <div className="space-y-10 mb-[30px]">
          <Slider
            label="Interest Rate (p.a)*"
            min={2}
            max={18}
            step={0.5}
            value={interest}
            onChange={setInterest}
            rangeData={[2, 4, 6, 8, 10, 12, 14, 16, 18]}
            unit="%"
          />
          <Slider
            label="Loan Tenure"
            min={12}
            max={108}
            step={1}
            value={tenure}
            onChange={setTenure}
            rangeData={[12, 24, 36, 48, 60, 72, 84, 96, 108]}
            unit="M"
          />
        </div>

        {/* Disclaimer */}
        <p className=" hidden sm:block text-[10px] leading-[1.4] text-[#424242] mt-[30px] w-full max-w-[90%]">
          Disclaimer*: Our Personal Loan EMI calculator offers estimated monthly
          installments which are indicative and tentative and are based upon
          the details populated by the user. Actual loan terms and eligibility
          are subject to bank approval. For precise loan details, consult our
          representatives before decisions based on these estimates.
        </p>
      </div>

      {/* RIGHT PIE CHART */}
      <div className="w-full md:w-[400px] bg-black rounded-3xl p-5 text-center relative mx-auto md:mx-0">
        <div className="relative w-full max-w-[280px] sm:max-w-[320px] mx-auto flex flex-col items-center justify-center">
          {/* Donut Chart with Thicker Ring */}
          <Doughnut
            data={chartData}
            options={{
              ...chartOptions,
              cutout: "60%", // smaller % = thicker ring
            }}
            className="rotate-45"
          />

          {/* Text ON the strip (mimicking screenshot) */}
           <div className="absolute top-[10%] left-[35%] [@media(max-width:340px)]:text-sm text-xl font-semibold text-black whitespace-nowrap">
            ₹{Math.round(totalInterest).toLocaleString()}*
          </div>
          <div className="absolute bottom-[5%] left-[35%] [@media(max-width:340px)]:text-sm text-xl font-semibold text-black whitespace-nowrap">
            ₹{amount.toLocaleString()}
          </div>
         
        </div>

        {/* Monthly EMI below */}
        <div className="text-center mt-4">
          <p className="text-md">Monthly EMI</p>
          <p className="text-3xl font-bold">₹{Math.round(emi).toLocaleString()}*</p>
        </div>

        {/* Legend */}
        <div className="relative flex justify-center flex-wrap gap-4 mt-6 text-sm text-gray-300">
          <div className="flex items-center gap-2">
            <span className="w-7 h-5 rounded-full bg-[#ccfce2]"></span>
            Principal Amount
          </div>
          <div className="flex items-center gap-2">
            <span className="w-7 h-5 rounded-full bg-[#00ff95]"></span>
            Interest Payable
          </div>
        </div>
      </div>

      <p className=" block text-[10px] leading-[1.4] text-[#424242] mt-[30px] w-full max-w-[95%] sm:hidden">
        Disclaimer*: Our Personal Loan EMI calculator offers estimated monthly
        installments which are indicative and tentative and are based upon
        the details populated by the user. Actual loan terms and eligibility
        are subject to bank approval. For precise loan details, consult our
        representatives before decisions based on these estimates.
      </p>
    </section>
  );
}
